import React, { useEffect, useState } from 'react';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table';
import dayjs from 'dayjs';
import { usePaymentObligationStore } from '../../store/usePaymentObligationStore';
import { usePaymentTypeStore } from '../../store/usePaymentTypeStore';
import { useFamilyStore } from '../../store/useFamilyStore';
import { Button, Label, Select, TextInput } from 'flowbite-react';
import { useCanAccessResource, useHasPermission, useIsMainAdmin } from '../../utils/permissions';
import { usePermissionStore } from '../../store/usePermissionStore';
import AccessDenied from '../../components/common/AccessDenied';

export default function OutstandingPaymentsPage() {
  const getPaymentObligations = usePaymentObligationStore((state) => state.getPaymentObligations);
  const obligations = usePaymentObligationStore((state) => state.obligations);
  const loading = usePaymentObligationStore((state) => state.loading);
  const pagination = usePaymentObligationStore((state) => state.pagination);
  const getOutstandingBalance = usePaymentObligationStore((state) => state.getOutstandingBalance);

  const getPaymentTypes = usePaymentTypeStore((state) => state.getPaymentTypes);
  const paymentTypes = usePaymentTypeStore((state) => state.paymentTypes);

  const getFamilies = useFamilyStore((state) => state.getFamilies);
  const families = useFamilyStore((state) => state.families);

  const canAccess = useCanAccessResource('payment-obligations');
  const hasReadPermission = useHasPermission('payment-obligations', 'read');
  const isMainAdmin = useIsMainAdmin();
  const isLoadingPermissions = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [search, setSearch] = useState<string>('');

  // Filters
  const [filterPaymentTypeId, setFilterPaymentTypeId] = useState<string>('');
  const [filterFamilyId, setFilterFamilyId] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterStartPeriod, setFilterStartPeriod] = useState<string>('');
  const [filterEndPeriod, setFilterEndPeriod] = useState<string>('');

  // Outstanding balance view
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [selectedPaymentTypeId, setSelectedPaymentTypeId] = useState<string>('');
  const [outstandingBalance, setOutstandingBalance] = useState<any>(null);
  const [showBalanceView, setShowBalanceView] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterPaymentTypeId, filterFamilyId, filterStatus, filterStartPeriod, filterEndPeriod]);

  useEffect(() => {
    if (hasFetched && !isLoadingPermissions && (hasReadPermission || isMainAdmin)) {
      getPaymentTypes();
      getFamilies();
    }
  }, [hasFetched, isLoadingPermissions, hasReadPermission, isMainAdmin, getPaymentTypes, getFamilies]);

  useEffect(() => {
    if (hasFetched && !isLoadingPermissions && (hasReadPermission || isMainAdmin)) {
      getPaymentObligations(
        search || undefined,
        currentPage,
        limit,
        filterPaymentTypeId || undefined,
        filterFamilyId || undefined,
        filterStatus || undefined,
        undefined,
        filterStartPeriod || undefined,
        filterEndPeriod || undefined
      );
    }
  }, [
    getPaymentObligations,
    search,
    currentPage,
    limit,
    filterPaymentTypeId,
    filterFamilyId,
    filterStatus,
    filterStartPeriod,
    filterEndPeriod,
    hasFetched,
    isLoadingPermissions,
    hasReadPermission,
    isMainAdmin,
  ]);

  const handleViewBalance = async () => {
    if (!selectedFamilyId || !selectedPaymentTypeId) {
      return;
    }
    const balance = await getOutstandingBalance(selectedFamilyId, selectedPaymentTypeId);
    if (balance) {
      setOutstandingBalance(balance);
      setShowBalanceView(true);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'pending':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  // Show loading while permissions are being fetched
  if (isLoadingPermissions || !hasFetched) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-lg text-gray-600">Loading permissions...</div>
        </div>
      </div>
    );
  }

  // Show permission denied message if no access
  if (!canAccess && !isMainAdmin) {
    return <AccessDenied resourceName="Outstanding Payments" />;
  }

  return (
    <>
      <PageMeta title="Outstanding Payments" description="View and manage outstanding payment obligations" />
      <PageBreadcrumb pageTitle="Outstanding Payments" />

      <ComponentCard title="Outstanding Payments">
        {/* Outstanding Balance View */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            View Outstanding Balance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="balanceFamilyId" value="Family *" style={{ fontFamily: 'Lato, sans-serif' }} />
              <Select
                id="balanceFamilyId"
                value={selectedFamilyId}
                onChange={(e) => setSelectedFamilyId(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                <option value="">Select Family</option>
                {families.map((family) => (
                  <option key={family._id} value={family._id}>
                    {family.familyName}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="balancePaymentTypeId" value="Payment Type *" style={{ fontFamily: 'Lato, sans-serif' }} />
              <Select
                id="balancePaymentTypeId"
                value={selectedPaymentTypeId}
                onChange={(e) => setSelectedPaymentTypeId(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                <option value="">Select Payment Type</option>
                {paymentTypes
                  .filter((pt) => pt.category === 'subscription' && pt.isActive)
                  .map((pt) => (
                    <option key={pt._id} value={pt._id}>
                      {pt.name}
                    </option>
                  ))}
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleViewBalance}
                disabled={!selectedFamilyId || !selectedPaymentTypeId || loading}
                className="w-full"
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                View Balance
              </Button>
            </div>
          </div>

          {showBalanceView && outstandingBalance && (
            <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg">
              <h4 className="font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Balance Summary
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Due</p>
                  <p className="text-lg font-bold">{outstandingBalance.totalDue.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Paid</p>
                  <p className="text-lg font-bold text-green-600">{outstandingBalance.totalPaid.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Remaining Balance</p>
                  <p className="text-lg font-bold text-red-600">{outstandingBalance.remainingBalance.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Obligations</p>
                  <p className="text-lg font-bold">
                    {outstandingBalance.summary.pending} pending, {outstandingBalance.summary.partial} partial,{' '}
                    {outstandingBalance.summary.paid} paid
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label htmlFor="search" value="Search" style={{ fontFamily: 'Lato, sans-serif' }} />
            <TextInput
              id="search"
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ fontFamily: 'Lato, sans-serif' }}
            />
          </div>
          <div>
            <Label htmlFor="filterPaymentTypeId" value="Payment Type" style={{ fontFamily: 'Lato, sans-serif' }} />
            <Select
              id="filterPaymentTypeId"
              value={filterPaymentTypeId}
              onChange={(e) => setFilterPaymentTypeId(e.target.value)}
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              <option value="">All Payment Types</option>
              {paymentTypes
                .filter((pt) => pt.category === 'subscription')
                .map((pt) => (
                  <option key={pt._id} value={pt._id}>
                    {pt.name}
                  </option>
                ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="filterFamilyId" value="Family" style={{ fontFamily: 'Lato, sans-serif' }} />
            <Select
              id="filterFamilyId"
              value={filterFamilyId}
              onChange={(e) => setFilterFamilyId(e.target.value)}
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              <option value="">All Families</option>
              {families.map((family) => (
                <option key={family._id} value={family._id}>
                  {family.familyName}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="filterStatus" value="Status" style={{ fontFamily: 'Lato, sans-serif' }} />
            <Select
              id="filterStatus"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="filterStartPeriod" value="Start Period" style={{ fontFamily: 'Lato, sans-serif' }} />
            <TextInput
              id="filterStartPeriod"
              type="text"
              placeholder="YYYY-MM or YYYY"
              value={filterStartPeriod}
              onChange={(e) => setFilterStartPeriod(e.target.value)}
              style={{ fontFamily: 'Lato, sans-serif' }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Payment Type</TableCell>
                <TableCell>Family</TableCell>
                <TableCell>Amount Due</TableCell>
                <TableCell>Amount Paid</TableCell>
                <TableCell>Remaining</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due Date</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="text-gray-600">Loading...</div>
                  </TableCell>
                </TableRow>
              ) : obligations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="text-gray-600">No outstanding payments found</div>
                  </TableCell>
                </TableRow>
              ) : (
                obligations.map((obligation) => {
                  const paymentType =
                    typeof obligation.paymentTypeId === 'string'
                      ? paymentTypes.find((pt) => pt._id === obligation.paymentTypeId)
                      : obligation.paymentTypeId;
                  const family =
                    typeof obligation.familyId === 'string'
                      ? families.find((f) => f._id === obligation.familyId)
                      : obligation.familyId;
                  const remaining = obligation.amountDue - obligation.amountPaid;

                  return (
                    <TableRow key={obligation._id}>
                      <TableCell>{obligation.period}</TableCell>
                      <TableCell>{paymentType?.name || 'N/A'}</TableCell>
                      <TableCell>{family?.familyName || 'N/A'}</TableCell>
                      <TableCell>
                        {paymentType?.currency || 'INR'} {obligation.amountDue.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {paymentType?.currency || 'INR'} {obligation.amountPaid.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <span className={remaining > 0 ? 'text-red-600 font-semibold' : 'text-green-600'}>
                          {paymentType?.currency || 'INR'} {remaining.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(obligation.status)}`}
                        >
                          {obligation.status}
                        </span>
                      </TableCell>
                      <TableCell>{dayjs(obligation.dueDate).format('MMM DD, YYYY')}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, pagination.total)} of{' '}
              {pagination.total} obligations
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={!pagination.hasPrevPage || loading}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                Previous
              </Button>
              <Button
                onClick={() => setCurrentPage((prev) => Math.min(pagination.totalPages, prev + 1))}
                disabled={!pagination.hasNextPage || loading}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </ComponentCard>
    </>
  );
}
